import { useState, useEffect, Fragment, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import usertableApi from 'api/usertable'
import categoryApi from 'api/category'
import { Switch } from '@headlessui/react'
import { enqueueSnackbar as enqueueSnackbarAction } from 'store/actions'
import { useDispatch } from 'react-redux'
import UploadAvatar from 'components/UploadAvatar'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import Select from '../components/Select'
import Toggle from '../components/Switch'
import TextField from '../components/TextField'
import Button from 'tailwindui/pages/components/Button'
import { SpinButton } from 'tailwindui/pages/components/Spin'

const modules = {
  toolbar: [
    [{ 'header': [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
    ['link', 'image', 'video'],
    ['clean']
  ],
}

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video'
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function EditTemplate({ isTable, isPopup, item, updateItem, isSheet }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    poster: '',
    icon: '',
    excerpt: '',
    base64Image: '',
    base64Image1: '',
    category: '',
    isTemplate: false,
    isFeatured: false,
    enrichWebhook: false
  })

  const getTemplate = useCallback(async () => {
    if (isPopup && item) {
      setTemplate(item)
      return
    }
    try {
      setLoading(true)
      if (isTable) {
        const result = await usertableApi.getUsertable(id)
        if (result && result.data) {
          setTemplate({
            name: result.data.name || '',
            description: result.data.description || '',
            excerpt: result.data.excerpt || '',
            poster: result.data.poster || '',
            icon: result.data.icon || '',
            category: result.data.category || '',
            isFeatured: result.data.isFeatured || false,
            isTemplate: result.data.isTemplate !== false,
            enrichWebhook: result.data.enrichWebhook || false
          })
        }
      } else {
        const result = isTable ? await usertableApi.getUsertable(id) : await usertableApi.getTemplate(id)
        if (result && result.data && result.data.table) {
          setTemplate({
            name: result.data.table.name || '',
            description: result.data.table.description || '',
            excerpt: result.data.table.excerpt || '',
            poster: result.data.table.poster || '',
            icon: result.data.table.icon || '',
            category: result.data.table.category || '',
            isFeatured: result.data.table.isFeatured || false,
            isTemplate: result.data.table.isTemplate !== false,
            enrichWebhook: result.data.table.enrichWebhook || false
          })
        }
      }
    } catch (error) {
      console.error(error)
      dispatch(enqueueSnackbarAction({
        message: 'Failed to load template',
        options: { variant: 'error' }
      }))
    } finally {
      setLoading(false)
    }
  }, [id, dispatch, item, isTable, isPopup])

  const getCategories = useCallback(async () => {
    try {
      const result = await categoryApi.getCategories()
      if (result && result.data) {
        setCategories(result.data.filter(cat => cat.isForTable))
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    getCategories()
    if (id) {
      getTemplate()
    } else {
      if (isPopup && item) {
        getTemplate()
      } else {
        navigate('/dashboard')
      }
    }
  }, [id, getTemplate, getCategories, navigate, isPopup, item])

  useEffect(() => {
    if (categories.length > 0 && template.category) {
      const category = categories.find(cat => cat._id === template.category?._id)
      setSelectedCategory(category || null)
    }
  }, [categories, template.category])

  const handleSubmit = async () => {
    try {
      setLoading(true)
      if (isPopup) {
        const templateData = {
          ...template,
          poster: template.base64Image ?? template.poster,
          icon: template.base64Image1 ?? template.icon
        }
        if(isSheet){
           templateData.sheetName = template.name
        }
        const result = await usertableApi.updateUsertable(templateData._id, templateData)
        if (result && result.data) {
          if (isSheet) {
            updateItem(result.data)
            dispatch({ type: 'UPDATE_SHEET', payload: result.data })
          } else {
            updateItem(result.data)
            dispatch({ type: 'SET_TABLE', payload: result.data })
          }
        }
        return
      }
      const templateData = {
        ...template,
        category: selectedCategory?._id,
        poster: template.base64Image ?? template.poster,
        icon: template.base64Image1 ?? template.icon
      }
      if (id) {
        await usertableApi.updateUsertable(id, templateData)
      }

      if (isTable) {
        dispatch(enqueueSnackbarAction({
          message: `Table ${id ? 'updated' : 'created'} successfully`,
          options: { variant: 'success' }
        }))
      } else {
        dispatch(enqueueSnackbarAction({
          message: `Template ${id ? 'updated' : 'created'} successfully`,
          options: { variant: 'success' }
        }))
      }
    } catch (error) {
      console.error(error)
      dispatch(enqueueSnackbarAction({
        message: `Failed to ${id ? 'update' : 'create'} `,
        options: { variant: 'error' }
      }))
    } finally {
      setLoading(false)
    }
  }

  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setTemplate(prev => ({
      ...prev,
      [name]: value
    }))
  }, [])

  const handleDescriptionChange = useCallback((content) => {
    setTemplate(prev => ({
      ...prev,
      description: content
    }))
  }, [])

  const handleImageChange = useCallback((image) => {
    setTemplate(prev => ({
      ...prev,
      base64Image: image
    }))
  }, [])
  const handleImageChange1 = useCallback((image) => {
    setTemplate(prev => ({
      ...prev,
      base64Image1: image
    }))
  }, [])

  const handleToggleChange = useCallback((field) => (checked) => {
    setTemplate(prev => ({
      ...prev,
      [field]: checked
    }))
  }, [])

  const handleCancel = useCallback(() => {
    if (isPopup) {
      updateItem(null)
    } else {
      navigate('/dashboard')
    }
  }, [navigate, isPopup, updateItem])
  if (isSheet) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-3 gap-8">
          <div className="col-span-2 space-y-6">
            <TextField label='Name' name='name' value={template.name} onChange={handleChange} />
          </div>
        </div>
        <div className="flex justify-end space-x-3 pt-8 gap-4">
          <Button
            variant="secondary"
            onClick={handleCancel}
            
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={loading}
            onClick={handleSubmit}
            
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

    )
  }
  const renderForm = () => {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-3 gap-8">
          <div className="col-span-2 space-y-6">
            <TextField label='Name' name='name' value={template.name} onChange={handleChange} />
            {!isTable && !isPopup ?
              <TextField label='Excerpt' type='textarea' name='excerpt' value={template.excerpt} onChange={handleChange} /> : null}
            {isPopup ?
              (<>
                <TextField label='Description' type='textarea' name='excerpt' value={template.excerpt} onChange={handleChange} />
                <Toggle label='Auto Enrich table' value={template.autoEnrich} onChange={handleToggleChange('autoEnrich')} />

              </>) :
              <div>
                <label className="block text-md font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <div className="border rounded-md">
                  <ReactQuill
                    theme="snow"
                    value={template.description}
                    onChange={handleDescriptionChange}
                    modules={modules}
                    formats={formats}
                    style={{ height: '300px' }}
                  />
                </div>
              </div>}
          </div>
          {isPopup ? null : <div className="space-y-6">
            {!isTable ? <div>
              <Select label='Category' value={selectedCategory} onChange={setSelectedCategory}
                options={categories}
              />
            </div> : null}
            {!isTable ? <div>
              <label className="block text-md font-semibold text-gray-700 mb-2">
                Template Image
              </label>
              <UploadAvatar
                base64Image={template.base64Image}
                setBase64Image={handleImageChange}
                url={template?.poster}
              />
            </div> : null}
            <div>
              <label className="block text-md font-semibold text-gray-700 mb-2">
                Icon
              </label>
              <UploadAvatar
                base64Image={template.base64Image1}
                setBase64Image={handleImageChange1}
                url={template?.icon}
              />
            </div>
            {isTable ? <div className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <Toggle label='Auto Enrich table' value={template.enrichWebhook} onChange={handleToggleChange('enrichWebhook')} />
              </div>


            </div>
              : <div className="space-y-4 pt-4">
                <div className="flex items-center justify-between">
                  <span className="flex flex-grow flex-col">
                    <span className="text-sm font-medium text-gray-900">Featured Template</span>
                    <span className="text-sm text-gray-500">
                      Mark this template as featured
                    </span>
                  </span>
                  <Switch
                    checked={template.isFeatured}
                    onChange={handleToggleChange('isFeatured')}
                    className={classNames(
                      template.isFeatured ? 'bg-indigo-600' : 'bg-gray-200',
                      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={classNames(
                        template.isFeatured ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                      )}
                    />
                  </Switch>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex flex-grow flex-col">
                    <span className="text-sm font-medium text-gray-900">Active Template</span>
                    <span className="text-sm text-gray-500">
                      Show/hide this template
                    </span>
                  </span>
                  <Switch
                    checked={template.isTemplate}
                    onChange={handleToggleChange('isTemplate')}
                    className={classNames(
                      template.isTemplate ? 'bg-indigo-600' : 'bg-gray-200',
                      'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={classNames(
                        template.isTemplate ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                      )}
                    />
                  </Switch>
                </div>
              </div>}
          </div>}
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse space-x-4">
          <div className="ml-5">
            {loading ?
              <SpinButton title={'Saving'}
              />
              :
              <Button
                variant='primary'
                onClick={handleSubmit}
                disabled={loading}
              >
                Save
              </Button>
            }
          </div>
          <Button
            variant='secondary'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    )
  }
  if (isPopup) {
    return renderForm()
  }
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-8">
              {isTable ? id ? 'Edit Template' : 'Create Template' : id ? 'Edit Template' : 'Create Template'}
            </h3>
            {renderForm()}
          </div>
        </div>
      </div>
    </div>
  )
}
